import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { apiError, money } from "@/lib/api";
import { printOrderReceipt, printPaidReceipt } from "@/lib/printing";
import { FloorCanvas, StatusLegend, withAutoPos, tableState } from "@/components/floor/FloorCanvas";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Plus, Minus, Trash2, Send, Printer, Banknote, CreditCard, ChevronLeft, StickyNote, Loader2, ArrowRightLeft, LayoutGrid, Map as MapIcon, CalendarCheck,
} from "lucide-react";

export const STATUS_META = {
  AVAILABLE: { label: "Свободна", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  OCCUPIED: { label: "Заета", cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  ORDER_SENT: { label: "Изпратена поръчка", cls: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-blue-500" },
  IN_PREPARATION: { label: "Приготвя се", cls: "bg-indigo-50 text-indigo-700 border-indigo-200", dot: "bg-indigo-500" },
  FOOD_READY: { label: "Храна готова", cls: "bg-green-100 text-green-800 border-green-300", dot: "bg-green-600" },
  BILL_PRINTED: { label: "Отпечатана сметка", cls: "bg-purple-50 text-purple-700 border-purple-200", dot: "bg-purple-500" },
  PAYMENT_PENDING: { label: "Очаква плащане", cls: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500" },
  OPEN: { label: "Отворена", cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  RESERVED: { label: "Резервирана", cls: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-blue-500" },
};

const NOTES = ["Без лук", "Без сол", "Добре препечено", "Допълнителен сос", "Без сос"];

function TableCard({ t, onClick, onReserve }) {
  const meta = STATUS_META[t.status] || STATUS_META.AVAILABLE;
  return (
    <div className="relative">
    <button onClick={onClick} data-testid={`table-card-${t.number}`}
      className="w-full bg-white rounded-2xl border border-gold/10 shadow-soft p-5 text-left hover:-translate-y-0.5 transition-transform active:scale-95">
      <div className="flex items-center justify-between">
        <span className="font-display text-3xl font-semibold text-forest-deep">{t.name || t.number}</span>
        <span className={`w-3 h-3 rounded-full ${meta.dot}`} />
      </div>
      <div className={`inline-block mt-2 text-xs px-2 py-1 rounded-lg border ${meta.cls}`}>{meta.label}</div>
      <div className="mt-3 text-sm text-muted-foreground">{t.zone} · {t.seats} места</div>
      {t.current_total > 0 && <div className="mt-2 text-lg font-bold text-forest-deep">{money(t.current_total)}</div>}
      {t.waitress_name && <div className="text-xs text-gold-dark mt-1">{t.waitress_name}</div>}
    </button>
    {!t.order_id && (
      <button onClick={onReserve} title={t.status === "RESERVED" ? "Махни резервацията" : "Резервирай"} data-testid={`table-reserve-${t.number}`}
        className={`absolute bottom-3 right-3 p-1.5 rounded-lg ${t.status === "RESERVED" ? "bg-blue-600 text-white" : "bg-cream-muted text-forest/60 hover:text-forest"}`}>
        <CalendarCheck className="w-4 h-4" />
      </button>
    )}
    </div>
  );
}


function TransferDialog({ open, onClose, order, tables, onDone }) {
  const free = tables.filter((t) => !t.order_id && t.id !== order.table_id);
  const go = async (t) => {
    try {
      await api.post(`/orders/${order.id}/transfer`, { table_id: t.id });
      toast.success(`Поръчката е преместена на маса ${t.name || t.number}`);
      onDone(t);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="transfer-dialog">
        <DialogHeader><DialogTitle>Премести поръчката на друга маса</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto">
          {free.map((t) => (
            <button key={t.id} onClick={() => go(t)} data-testid={`transfer-to-${t.number}`}
              className="p-3 rounded-xl border border-gold/30 hover:bg-forest hover:text-white transition-colors">
              <div className="font-bold">{t.name || t.number}</div><div className="text-[11px] opacity-70">{t.zone}{t.status === "RESERVED" ? " · резервирана" : ""}</div>
            </button>
          ))}
          {free.length === 0 && <p className="col-span-3 text-sm text-muted-foreground">Няма свободни маси</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OrderView({ table, order, products, categories, tables, onBack, reload, onTransferred }) {
  const [activeCat, setActiveCat] = useState(categories[0]?.id);
  const [cart, setCart] = useState([]);
  const [noteFor, setNoteFor] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [transferOpen, setTransferOpen] = useState(false);

  useEffect(() => {
    if (!activeCat && categories.length) setActiveCat(categories[0].id);
  }, [categories, activeCat]);

  const catProducts = products.filter((p) => p.category_id === activeCat && p.available);

  const addToCart = (p) => {
    setCart((c) => {
      const ex = c.find((x) => x.product_id === p.id && !x.note);
      if (ex) return c.map((x) => (x === ex ? { ...x, quantity: x.quantity + 1 } : x));
      return [...c, { uid: `${p.id}-${Date.now()}`, product_id: p.id, name: p.name, name_bg: p.name_bg, price: p.price, quantity: 1, note: "", send_to_kitchen: p.send_to_kitchen }];
    });
  };
  const changeQty = (idx, d) => setCart((c) => c.map((x, i) => i === idx ? { ...x, quantity: Math.max(1, x.quantity + d) } : x));
  const removeItem = (idx) => setCart((c) => c.filter((_, i) => i !== idx));
  const setNote = (idx, note) => setCart((c) => c.map((x, i) => i === idx ? { ...x, note } : x));

  const cartTotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  const sendOrder = async () => {
    if (cart.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/batch`, {
        items: cart.map((c) => ({ product_id: c.product_id, quantity: c.quantity, note: c.note })),
      });
      toast.success(data.sent_to_kitchen > 0 ? `Изпратено в кухнята (${data.sent_to_kitchen} артикула)` : "Добавено към поръчката");
      setCart([]);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const doPrintBill = async () => {
    try {
      await api.post(`/orders/${order.id}/print-bill`);
      await printOrderReceipt(order, { prebill: true });
      toast.success("Сметката е изпратена за печат. Масата остава отворена.");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmPayment = async (method) => {
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/pay`, { method, discount_pct: Number(discount) || 0 });
      const { data: paid } = await api.get(`/orders/${order.id}`);
      toast.success("Плащането е записано. Масата е свободна.");
      setPayOpen(false);
      onBack();
      reload();
      await printPaidReceipt(paid);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col lg:flex-row h-full">
      <TransferDialog open={transferOpen} onClose={() => setTransferOpen(false)} order={order} tables={tables}
        onDone={(t) => { setTransferOpen(false); onTransferred(t); }} />
      {/* Menu */}
      <div className="flex-1 flex flex-col min-w-0 no-print">
        <div className="flex items-center gap-3 p-4 border-b border-gold/10 bg-white">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="pos-back"><ChevronLeft /></Button>
          <div className="flex-1">
            <div className="font-display text-2xl text-forest-deep">Маса {table.name || table.number}</div>
            <div className="text-xs text-muted-foreground">{table.zone}</div>
          </div>
          {order.items?.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setTransferOpen(true)} className="gap-1 rounded-xl" data-testid="pos-transfer">
              <ArrowRightLeft className="w-4 h-4" /> Премести
            </Button>
          )}
        </div>
        <div className="flex gap-2 p-3 overflow-x-auto bg-white border-b border-gold/10">
          {categories.map((c) => (
            <button key={c.id} onClick={() => setActiveCat(c.id)}
              className={`px-4 py-2.5 rounded-xl text-sm whitespace-nowrap transition-colors ${activeCat === c.id ? "bg-forest text-white" : "bg-cream-muted text-forest hover:bg-gold/20"}`}
              data-testid={`pos-cat-${c.id}`}>
              {c.name_bg || c.name}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 content-start">
          {catProducts.map((p) => (
            <button key={p.id} onClick={() => addToCart(p)} data-testid={`pos-product-${p.id}`}
              className="bg-white rounded-2xl border border-gold/10 shadow-soft p-4 text-left hover:border-gold active:scale-95 transition-all min-h-[92px] flex flex-col justify-between">
              <div className="font-medium text-forest-deep text-sm">{p.name_bg || p.name}</div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-gold-dark font-bold">{money(p.price)}</span>
                {!p.send_to_kitchen && <span className="text-[10px] text-muted-foreground">без кухня</span>}
              </div>
            </button>
          ))}
          {catProducts.length === 0 && <div className="text-muted-foreground text-sm">Няма продукти в тази категория</div>}
        </div>
      </div>

      {/* Cart */}
      <div className="w-full lg:w-96 bg-cream-muted border-l border-gold/10 flex flex-col no-print" data-testid="pos-cart">
        <div className="p-4 border-b border-gold/10 flex items-center justify-between">
          <span className="font-semibold text-forest-deep">Нова поръчка (кошница)</span>
          <span className="text-sm bg-forest text-white px-2 py-0.5 rounded-lg" data-testid="cart-count">{cart.reduce((s, i) => s + i.quantity, 0)}</span>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {cart.length === 0 && <div className="text-sm text-muted-foreground text-center mt-8">Изберете продукти от менюто</div>}
          {cart.map((it, idx) => (
            <div key={it.uid} className="bg-white rounded-xl p-3 border border-gold/10">
              <div className="flex justify-between items-start">
                <div className="font-medium text-forest-deep text-sm flex-1">{it.name_bg || it.name}</div>
                <button onClick={() => removeItem(idx)} className="text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-2">
                  <button onClick={() => changeQty(idx, -1)} className="w-8 h-8 rounded-lg bg-cream-muted flex items-center justify-center active:scale-90"><Minus className="w-4 h-4" /></button>
                  <span className="w-6 text-center font-semibold">{it.quantity}</span>
                  <button onClick={() => changeQty(idx, 1)} className="w-8 h-8 rounded-lg bg-cream-muted flex items-center justify-center active:scale-90"><Plus className="w-4 h-4" /></button>
                </div>
                <span className="font-semibold text-forest-deep">{money(it.price * it.quantity)}</span>
              </div>
              <button onClick={() => setNoteFor(idx)} className="flex items-center gap-1 text-xs text-gold-dark mt-2">
                <StickyNote className="w-3 h-3" /> {it.note || "Добави бележка"}
              </button>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-gold/10 bg-white space-y-2">
          <div className="flex justify-between text-sm"><span>Кошница</span><span className="font-bold">{money(cartTotal)}</span></div>
          <Button onClick={sendOrder} disabled={cart.length === 0 || busy} data-testid="pos-send-order"
            className="w-full h-14 bg-forest hover:bg-forest-light text-white rounded-xl text-base gap-2">
            {busy ? <Loader2 className="animate-spin" /> : <Send className="w-5 h-5" />} Изпрати в кухнята
          </Button>
          <div className="gold-rule my-2" />
          {order.items?.length > 0 && (
            <details className="text-sm" data-testid="pos-ordered-items">
              <summary className="cursor-pointer text-forest font-medium">Вече поръчано ({order.items.reduce((s, i) => s + i.quantity, 0)})</summary>
              <div className="max-h-40 overflow-y-auto mt-1 space-y-0.5">
                {order.items.map((i) => (
                  <div key={i.id} className="flex justify-between text-xs"><span>{i.quantity} × {i.name_bg || i.name}{i.note ? ` (${i.note})` : ""}</span><span>{money(i.price * i.quantity)}</span></div>
                ))}
              </div>
            </details>
          )}
          <div className="flex justify-between text-sm"><span>Общо на масата</span><span className="font-bold text-lg text-forest-deep" data-testid="order-total">{money(order.total)}</span></div>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={doPrintBill} variant="outline" data-testid="pos-print-bill"
              className="h-12 rounded-xl border-forest text-forest gap-2"><Printer className="w-4 h-4" /> Печат сметка</Button>
            <Button onClick={() => setPayOpen(true)} data-testid="pos-pay"
              className="h-12 rounded-xl bg-gold hover:bg-gold-dark text-forest-deep gap-2">Плащане</Button>
          </div>
        </div>
      </div>

      {/* order items history */}
      <Dialog open={noteFor !== null} onOpenChange={(o) => !o && setNoteFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Бележка към артикула</DialogTitle></DialogHeader>
          <div className="flex flex-wrap gap-2">
            {NOTES.map((n) => (
              <button key={n} onClick={() => { setNote(noteFor, n); setNoteFor(null); }}
                className="px-3 py-2 rounded-xl bg-cream-muted text-forest text-sm hover:bg-gold/20">{n}</button>
            ))}
          </div>
          <Input placeholder="Друга бележка…" onKeyDown={(e) => { if (e.key === "Enter") { setNote(noteFor, e.target.value); setNoteFor(null); } }} />
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Плащане · Маса {table.number}</DialogTitle></DialogHeader>
          <div className="text-center text-3xl font-bold text-forest-deep" data-testid="pay-total">{money(order.total * (1 - (Number(discount) || 0) / 100))}</div>
          <div className="flex items-center justify-center gap-2 text-sm">
            <span>Отстъпка %</span>
            <Input type="number" min={0} max={100} value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-20 h-9" data-testid="pay-discount" />
          </div>
          <p className="text-center text-sm text-muted-foreground">Изберете метод след като клиентът плати</p>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Button onClick={() => confirmPayment("CASH")} disabled={busy} data-testid="pay-cash"
              className="h-20 flex-col gap-2 bg-forest hover:bg-forest-light text-white rounded-xl">
              <Banknote className="w-7 h-7" /> В брой
            </Button>
            <Button onClick={() => confirmPayment("CARD")} disabled={busy} data-testid="pay-card"
              className="h-20 flex-col gap-2 bg-forest hover:bg-forest-light text-white rounded-xl">
              <CreditCard className="w-7 h-7" /> С карта
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function POS() {
  const [tables, setTables] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(null); // { table, order }
  const [view, setView] = useState(localStorage.getItem("vb_pos_view") || "grid");
  const [zone, setZone] = useState(null);
  const selRef = useRef(null);
  selRef.current = selected;

  const loadTables = useCallback(async () => {
    try {
      const { data } = await api.get("/tables");
      setTables(data);
      if (selRef.current) {
        const { data: o } = await api.get(`/orders/${selRef.current.order.id}`);
        setSelected((s) => s ? { ...s, order: o } : s);
      }
    } catch (e) { console.error("Failed to load tables:", apiError(e)); }
  }, []);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    loadTables();
    const iv = setInterval(loadTables, 4000);
    return () => clearInterval(iv);
  }, [loadTables]);

  const openTable = async (t) => {
    try {
      const { data } = await api.post(`/tables/${t.id}/open`);
      setSelected({ table: t, order: data });
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleReserve = async (t) => {
    try { await api.post(`/tables/${t.id}/reserved`, { reserved: t.status !== "RESERVED" }); loadTables(); } catch (e) { toast.error(apiError(e)); }
  };
  const switchView = (v) => { setView(v); localStorage.setItem("vb_pos_view", v); };
  const zones = [...new Set(tables.map((t) => t.zone || "Основна зала"))];
  const curZone = zone && zones.includes(zone) ? zone : zones[0];

  if (selected) {
    return (
      <div className="h-[calc(100vh-4rem)]">
        <OrderView table={selected.table} order={selected.order} products={products}
          categories={categories} tables={tables}
          onTransferred={(t) => setSelected((s) => ({ ...s, table: t }))}
          onBack={() => setSelected(null)} reload={loadTables} />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-4xl text-forest-deep">Ресторант · Маси</h1>
        <div className="flex gap-1 bg-cream-muted rounded-xl p-1">
          <Button size="sm" variant={view === "grid" ? "default" : "ghost"} onClick={() => switchView("grid")} className="gap-1 rounded-lg" data-testid="pos-view-grid"><LayoutGrid className="w-4 h-4" /> Списък</Button>
          <Button size="sm" variant={view === "plan" ? "default" : "ghost"} onClick={() => switchView("plan")} className="gap-1 rounded-lg" data-testid="pos-view-plan"><MapIcon className="w-4 h-4" /> План</Button>
        </div>
      </div>
      <div className="gold-rule w-24 my-4" />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {zones.map((z) => (
          <button key={z} onClick={() => setZone(z)} data-testid={`pos-zone-${z}`}
            className={`px-4 py-1.5 rounded-full text-sm border ${z === curZone ? "bg-forest text-white border-forest" : "bg-white border-gold/30 text-forest"}`}>{z}</button>
        ))}
        <div className="ml-auto"><StatusLegend /></div>
      </div>
      {view === "plan" ? (
        <FloorCanvas tables={withAutoPos(tables.filter((t) => (t.zone || "Основна зала") === curZone))} onOpen={openTable} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-4">
          {tables.filter((t) => (t.zone || "Основна зала") === curZone).map((t) => (
            <TableCard key={t.id} t={{ ...t, status: tableState(t) === "RESERVED" ? "RESERVED" : t.status }} onClick={() => openTable(t)} onReserve={() => toggleReserve(t)} />
          ))}
        </div>
      )}
    </div>
  );
}
